/**
 * Detail panel for the row selected in the ladder table.
 *
 * It sits BELOW the table at full page width. In a right-hand column it was
 * taking a third of the viewport from the widest thing on the page, and
 * getting a 340px strip to stack four sections in — the worst of both.
 *
 * Four sections, in the order an operator triages: 实测流量 (what actually
 * happened) → 毛利 (what it earned) → 供应结构 (how much room there is) →
 * 链路 (who is wired to whom). The declared and the measured numbers are
 * labelled separately throughout — `配置权重` vs `实测承接` — and are never
 * summed, averaged, or substituted for one another.
 *
 * ── The one thing this panel must never do ──
 *
 * An `UpstreamModelNode` has no traffic of its own: `/v1/ops/traffic/overview`
 * has no (channel × model) group, so it borrows its channel's figures and
 * says so in `trafficProvenance`. The traffic section reads that field and
 * changes its caption accordingly. Rendering a borrowed rate under the plain
 * 实测流量 heading would be a real number attached to the wrong subject —
 * the same class of lie as printing 0% for an unsettled margin.
 *
 * The divergence callout is the reason the declared and the measured sit on
 * one panel. It is a direct subtraction now that `skuChannels` measures a
 * leg's real share — the one-directional inequality it used to need is gone
 * along with the bound it was built on. Its threshold lives in
 * `divergence.ts`.
 */

import { ExternalLink, MousePointerClick, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatCnyCents } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Chip, EmptyState, Panel, buttonClass } from '../../ui/index.ts';
import { findDivergence } from './divergence.ts';
import {
  GRADE_CHIP_TONE,
  GRADE_LABEL_KEY,
  downstreamDetailIsPrintable,
  formatDurationMs,
  formatEndpoint,
  formatInteger,
  formatMaybeInteger,
  formatPercentPoints,
  formatRate,
  nodeDisplayLabel,
} from './display.ts';
import { RefusalBreakdown } from './RefusalBreakdown.tsx';
import { supplyFailuresOf, supplyRateOf } from './model-nodes.ts';
import { VERDICT_TEXT_CLASS, legVerdict } from './supplyVerdict.ts';
import type {
  EdgeRole,
  MapNode,
  NodeMoney,
  PerspectiveModel,
  SkuNode,
  SupplyChannelNode,
  SupplyLink,
  UpstreamModelNode,
} from './types.ts';

const COVERAGE_KEY: Readonly<Record<'covered' | 'single_point' | 'unrouted', I18nKey>> = {
  covered: 'health_perspective.node.coverage.covered',
  single_point: 'health_perspective.node.coverage.single_point',
  unrouted: 'health_perspective.node.coverage.unrouted',
};

const POLICY_KEY: Readonly<Record<'full_chain' | 'network_only' | 'same_cache_domain' | 'fail_fast', I18nKey>> = {
  full_chain: 'health_perspective.node.policy.full_chain',
  network_only: 'health_perspective.node.policy.network_only',
  same_cache_domain: 'health_perspective.node.policy.same_cache_domain',
  fail_fast: 'health_perspective.node.policy.fail_fast',
};

const ROLE_CHIP_TONE: Readonly<Record<EdgeRole, 'signal' | 'neutral'>> = {
  active: 'signal',
  reserve: 'neutral',
  disabled: 'neutral',
};

/**
 * Role labels are the ladder table's, so a rung and this panel name the same
 * thing the same way. `active` alone carries the declared share, because it
 * is 0 by definition for the other two.
 */
const ROLE_LABEL_KEY: Readonly<Record<'reserve' | 'disabled', I18nKey>> = {
  reserve: 'health_perspective.table.rung.reserve',
  disabled: 'health_perspective.table.rung.disabled',
};

interface Props {
  readonly model: PerspectiveModel;
  readonly selectedNodeId: string | null;
}

export function NodeInspector({ model, selectedNodeId }: Props): JSX.Element {
  const t = useT();
  const node = findNode(model, selectedNodeId);

  if (node === null) {
    // No `Panel` here on purpose: its caption would repeat the empty
    // state's own title word for word, and DESIGN.md §1 allows one heading
    // recipe per section, not the same sentence twice.
    return (
      <div
        className="rounded-lg border border-line-soft bg-card"
        data-testid="health-perspective-inspector"
      >
        <EmptyState
          icon={<MousePointerClick aria-hidden="true" className="h-5 w-5" />}
          title={t('health_perspective.inspector.empty.title')}
          hint={t('health_perspective.inspector.empty.hint')}
        />
      </div>
    );
  }

  return (
    <Panel
      caption={nodeDisplayLabel(node, t)}
      actions={<Chip tone={GRADE_CHIP_TONE[node.grade]}>{t(GRADE_LABEL_KEY[node.grade])}</Chip>}
      padded
      testId="health-perspective-inspector"
      className="min-w-0"
    >
      <div className="flex flex-col gap-5" data-testid={`health-perspective-inspector-${node.id}`}>
        {/* Full width across the page, so the callout keeps its own line and
            the four sections sit side by side instead of stacking into a
            340px strip the way they did when this lived in a side column. */}
        <DivergenceCallout model={model} node={node} />
        <div className="grid min-w-0 gap-x-8 gap-y-5 md:grid-cols-2 xl:grid-cols-4">
          <TrafficSection node={node} />
          {node.kind === 'sku' || node.kind === 'channel' ? <MoneySection money={node.money} /> : null}
          {node.kind === 'sku' ? <SkuSupplySection node={node} /> : null}
          {node.kind === 'channel' ? <ChannelSupplySection node={node} /> : null}
          {node.kind === 'upstream_model' ? <UpstreamModelSupplySection node={node} /> : null}
          {node.kind === 'namespace' ? null : <ChainSection model={model} node={node} />}
        </div>
        <DeepLinks model={model} node={node} />
      </div>
    </Panel>
  );
}

function findNode(model: PerspectiveModel, id: string | null): MapNode | null {
  if (id === null || id.length === 0) return null;
  return (
    model.namespaces.find((n) => n.id === id) ??
    model.skus.find((n) => n.id === id) ??
    model.channels.find((n) => n.id === id) ??
    model.upstreamModels.find((n) => n.id === id) ??
    null
  );
}

function DivergenceCallout({
  model,
  node,
}: {
  readonly model: PerspectiveModel;
  readonly node: MapNode;
}): JSX.Element | null {
  const t = useT();
  if (node.kind !== 'sku' && node.kind !== 'upstream_model') return null;
  const finding = findDivergence(model, node);
  if (finding === null) return null;
  return (
    <div
      className="flex gap-2.5 rounded-lg border border-warn-line bg-warn-dim/40 p-3"
      data-testid="health-perspective-divergence"
    >
      <TriangleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
      <div className="min-w-0">
        <p className="m-0 text-body font-medium text-warn">
          {t('health_perspective.inspector.divergence.title')}
        </p>
        <p className="m-0 mt-1 text-dense text-paper-mid">
          {t('health_perspective.inspector.divergence.hint', {
            configured: formatPercentPoints(finding.configuredPct),
            observed: formatPercentPoints(finding.measuredPct),
          })}
        </p>
        {/* Name the LEG, not just the model: `gpt-image-2` appears on three
            channels of this SKU's ladder and only one of them is the finding. */}
        <p className="m-0 mt-1 font-mono text-micro text-paper-low">
          {nodeDisplayLabel(finding.sku, t)} →{' '}
          {formatEndpoint(
            {
              upstreamName: finding.link.upstreamName ?? finding.target.upstreamName,
              channelSlug: finding.link.channelSlug ?? finding.target.channelSlug,
              upstreamModelName: finding.link.upstreamModelName ?? finding.target.upstreamModelName,
            },
            t('health_perspective.table.endpoint_unknown'),
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * Measured traffic — except on an upstream model, where it is the channel's
 * and the caption says so out loud rather than in a tooltip.
 */
function TrafficSection({ node }: { readonly node: MapNode }): JSX.Element {
  const t = useT();
  const borrowed = node.kind === 'upstream_model' && node.trafficProvenance === 'channel_borrowed';
  return (
    <Section
      caption={t(
        borrowed
          ? 'health_perspective.inspector.section.traffic_channel'
          : 'health_perspective.inspector.section.traffic',
      )}
    >
      {borrowed ? (
        <p
          className="m-0 text-micro text-paper-low"
          data-testid="health-perspective-inspector-borrowed"
        >
          {t('health_perspective.inspector.borrowed_note')}
        </p>
      ) : null}
      <Row label={t('health_perspective.node.requests')} value={formatInteger(node.traffic.requests)} />
      <Row label={t('health_perspective.node.observed_share')} value={formatRate(node.traffic.observedShare)} />
      {/* Three subjects, each over its own population, none derived from
          another by subtraction except the one `types.ts` says is sound
          (`attributableErrors = errors - refusedBeforeUpstream`). */}
      {/* The fraction only when it is one — with the counter but no code
          split there IS a fraction, computed by deducting nothing, and the
          ladder table replaces it with the caveat for exactly that reason.
          Every `channel` and `upstream_model` node reaches this branch:
          `supply.channels` never carries `refusedByCode`. */}
      <Row
        label={t('health_perspective.node.downstream_error_rate')}
        value={formatRate(node.traffic.downstreamErrorRate)}
        note={
          downstreamDetailIsPrintable(node.traffic)
            ? `${formatMaybeInteger(node.traffic.attributableErrors)} / ${formatMaybeInteger(node.traffic.attributableRequests)}`
            : t('health_perspective.refusal.unknown_short')
        }
        testId="health-perspective-inspector-downstream"
      />
      <Row
        label={t('health_perspective.node.refused_before_upstream')}
        value={formatMaybeInteger(node.traffic.refusedBeforeUpstream)}
        note={
          node.traffic.expectedRefusals === null
            ? t('health_perspective.refusal.unknown_short')
            : t('health_perspective.refusal.entry', {
                label: t('health_perspective.refusal.excluded'),
                count: formatInteger(node.traffic.expectedRefusals),
              })
        }
      />
      <Row
        label={t('health_perspective.node.tenant_error_rate')}
        value={formatRate(node.traffic.errorRate)}
        note={formatInteger(node.traffic.errors)}
      />
      <Row
        label={t('health_perspective.node.rate_limited_rate')}
        value={formatRate(node.traffic.rateLimitedRate)}
        note={formatInteger(node.traffic.rateLimited)}
      />
      {/* Upstream 429s the ladder absorbed (#2240) — pool capacity the
          tenant never saw, and NOT part of the row above. Unknown when the
          gateway does not report them, never zero. */}
      <Row
        label={t('health_perspective.node.upstream_rate_limited')}
        value={formatMaybeInteger(node.traffic.upstreamAttemptRateLimited)}
        note={t('health_perspective.node.upstream_rate_limited_note', {
          attempts: formatMaybeInteger(node.traffic.upstreamAttemptFailures),
          refused: formatMaybeInteger(node.traffic.upstreamRefused),
        })}
        testId="health-perspective-inspector-upstream-rate-limited"
      />
      {/* The #2240 supply figure over dials where reported; the 5xx floor
          where not. `supplyRateOf` decides, same as the table and the strip. */}
      <Row
        label={t('health_perspective.node.server_error_rate')}
        value={formatRate(supplyRateOf(node.traffic))}
        note={formatInteger(supplyFailuresOf(node.traffic))}
        testId="health-perspective-inspector-supply"
      />
      {node.traffic.refusedBeforeUpstream === null ||
      node.traffic.refusedBeforeUpstream === 0 ? null : (
        <RefusalBreakdown
          groups={node.traffic.refusedByCode}
          testId="health-perspective-inspector-refusals"
        />
      )}
      {/* Quoted against 5xx, not against requests: a fallback only ever had
          the chance to catch a request that reached an upstream. */}
      <Row
        label={t('health_perspective.node.fallback_rate')}
        value={formatRate(node.traffic.fallbackRate)}
        note={
          node.traffic.fallbacks > 0
            ? t('health_perspective.table.cell.supply_detail', {
                errors: formatInteger(supplyFailuresOf(node.traffic)),
                fallbacks: formatInteger(node.traffic.fallbacks),
              })
            : undefined
        }
      />
      <Row label={t('health_perspective.node.first_byte')} value={formatDurationMs(node.traffic.avgFirstByteMs)} />
    </Section>
  );
}

function MoneySection({ money }: { readonly money: NodeMoney }): JSX.Element {
  const t = useT();
  if (!money.settled) {
    return (
      <Section caption={t('health_perspective.inspector.section.money')}>
        <p className="m-0 text-dense text-paper-mid" data-testid="health-perspective-inspector-unsettled">
          {t('health_perspective.summary.unsettled')}
        </p>
        <p className="m-0 text-micro text-paper-low">{t('health_perspective.summary.unsettled_hint')}</p>
      </Section>
    );
  }
  return (
    <Section caption={t('health_perspective.inspector.section.money')}>
      <Row label={t('health_perspective.node.margin')} value={formatRate(money.marginPct)} />
      <Row label={t('health_perspective.node.revenue')} value={formatMoney(money.revenueCnyCents)} />
      <Row label={t('health_perspective.node.cost')} value={formatMoney(money.upstreamCostCnyCents)} />
      <Row label={t('health_perspective.node.profit')} value={formatMoney(money.profitCnyCents)} />
    </Section>
  );
}

function formatMoney(cents: number | null): string {
  return cents === null ? '—' : formatCnyCents(cents, false);
}

function SkuSupplySection({ node }: { readonly node: SkuNode }): JSX.Element {
  const t = useT();
  const coverageStatus = node.coverageStatus;
  return (
    <Section caption={t('health_perspective.inspector.section.supply')}>
      <Row
        label={t('health_perspective.node.coverage')}
        value={node.coverage === null ? '—' : formatInteger(node.coverage)}
        note={coverageStatus === null ? undefined : t(COVERAGE_KEY[coverageStatus])}
      />
      <Row label={t('health_perspective.node.targets_active')} value={formatInteger(node.activeTargetCount)} />
      <Row label={t('health_perspective.node.targets_reserve')} value={formatInteger(node.reserveTargetCount)} />
      <Row label={t('health_perspective.node.targets_disabled')} value={formatInteger(node.disabledTargetCount)} />
      <Row
        label={t('health_perspective.node.policy')}
        // A null policy is the gateway's `full_chain` default, not an
        // unknown — `types.ts` says so explicitly.
        value={t(POLICY_KEY[node.policyMode ?? 'full_chain'])}
      />
    </Section>
  );
}

function ChannelSupplySection({ node }: { readonly node: SupplyChannelNode }): JSX.Element {
  const t = useT();
  return (
    <Section caption={t('health_perspective.inspector.section.supply')}>
      <Row label={t('health_perspective.node.serves_skus')} value={formatInteger(node.servesSkuCount)} />
      {/* The count that decides how much a channel-level rate can be trusted:
          one error rate is shared by every model on the channel. */}
      <Row label={t('health_perspective.node.model_count')} value={formatInteger(node.modelCount)} />
    </Section>
  );
}

function UpstreamModelSupplySection({ node }: { readonly node: UpstreamModelNode }): JSX.Element {
  const t = useT();
  return (
    <Section caption={t('health_perspective.inspector.section.supply')}>
      <Row label={t('health_perspective.node.serves_skus')} value={formatInteger(node.servesSkuCount)} />
      <Row
        label={t('health_perspective.node.channel')}
        value={node.channelSlug ?? t('health_perspective.table.endpoint_unknown')}
      />
      <Row
        label={t('health_perspective.node.upstream_model')}
        value={node.upstreamModelName ?? t('health_perspective.table.endpoint_unknown')}
      />
      <Row
        label={t('health_perspective.node.capability')}
        value={node.capabilityStatus ?? '—'}
      />
    </Section>
  );
}

/**
 * The ladder section. For a SKU it lists that SKU's rungs; for an upstream
 * model it lists the SKUs that would attempt it; for a channel it lists every
 * rung landing on any model of that channel. All three are ordered by
 * retry-ladder position first (`priority` ascending — lower goes first) and
 * then by weight descending, so the row an operator reads first is the one
 * traffic reaches first.
 */
function ChainSection({
  model,
  node,
}: {
  readonly model: PerspectiveModel;
  readonly node: SkuNode | SupplyChannelNode | UpstreamModelNode;
}): JSX.Element {
  const t = useT();
  const channelTargetIds = new Set(
    model.upstreamModels
      .filter((target) => node.kind === 'channel' && target.channelNodeId === node.id)
      .map((target) => target.id),
  );
  const links = model.links
    .filter((link) => {
      if (node.kind === 'sku') return link.fromId === node.id;
      if (node.kind === 'upstream_model') return link.toId === node.id;
      return channelTargetIds.has(link.toId);
    })
    .sort((a, b) => (a.priority !== b.priority ? a.priority - b.priority : b.weight - a.weight));

  return (
    <Section caption={t('health_perspective.inspector.section.chain')}>
      {links.length === 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid="health-perspective-chain-empty">
          {t('health_perspective.inspector.chain.empty')}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {links.map((link) => (
            <ChainRow key={link.id} link={link} label={counterpartLabel(model, link, node, t)} />
          ))}
        </ul>
      )}
    </Section>
  );
}

/**
 * What the other end of the rung is called.
 *
 * From a SKU the counterpart is the ENDPOINT — `channelSlug /
 * upstreamModelName`, both halves — because "which model on which channel is
 * carrying this SKU" is the question the page exists to answer, and a bare
 * upstream name was the omission that made the previous view useless. From
 * the supply side it is the SKU.
 */
function counterpartLabel(
  model: PerspectiveModel,
  link: SupplyLink,
  node: SkuNode | SupplyChannelNode | UpstreamModelNode,
  t: (key: I18nKey) => string,
): string {
  if (node.kind === 'sku') {
    const target = model.upstreamModels.find((candidate) => candidate.id === link.toId);
    return formatEndpoint(
      {
        upstreamName: link.upstreamName ?? target?.upstreamName ?? null,
        channelSlug: link.channelSlug ?? target?.channelSlug ?? null,
        upstreamModelName: link.upstreamModelName ?? target?.upstreamModelName ?? null,
      },
      t('health_perspective.table.endpoint_unknown'),
    );
  }
  return model.skus.find((sku) => sku.id === link.fromId)?.label ?? link.fromId;
}

function ChainRow({ link, label }: { readonly link: SupplyLink; readonly label: string }): JSX.Element {
  const t = useT();
  return (
    <li
      className="rounded-md border border-line-soft bg-well px-3 py-2"
      data-testid={`health-perspective-chain-${link.id}`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 flex-1 truncate font-mono text-dense text-paper" title={label}>
          {label}
        </span>
        <Chip tone={ROLE_CHIP_TONE[link.role]} lamp={false}>
          {link.role === 'active'
            ? t('health_perspective.table.rung.active', {
                pct: formatPercentPoints(link.configuredSharePct),
              })
            : t(ROLE_LABEL_KEY[link.role])}
        </Chip>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-micro text-paper-low">
        {/* What this rung actually carried FOR THIS SKU. A null leg is not a
            zero: it is either "sent nothing" or "this gateway cannot say",
            and neither renders as a clean rung. */}
        {link.measured === null || link.measured.requests === 0 ? (
          <span>{t('health_perspective.table.rung.no_traffic')}</span>
        ) : (
          <>
            <span>
              {t('health_perspective.table.rung.volume', {
                requests: formatInteger(link.measured.requests),
                share: formatRate(link.measured.shareOfSku),
              })}
            </span>
            {link.measured.failureRate === null || link.measured.attributableErrors === null ? (
              <span className="text-warn">{t('health_perspective.table.rung.failed_unknown')}</span>
            ) : (
              <span className={VERDICT_TEXT_CLASS[legVerdict(link.measured)]}>
                {t('health_perspective.table.rung.failed', {
                  errors: formatInteger(link.measured.attributableErrors),
                  rate: formatRate(link.measured.failureRate),
                })}
              </span>
            )}
          </>
        )}
        <span>{t('health_perspective.inspector.chain.priority', { value: link.priority })}</span>
        {link.capabilityStatus === null ? null : (
          <span>{t('health_perspective.inspector.chain.capability', { status: link.capabilityStatus })}</span>
        )}
      </div>
    </li>
  );
}

function DeepLinks({
  model,
  node,
}: {
  readonly model: PerspectiveModel;
  readonly node: MapNode;
}): JSX.Element {
  const t = useT();
  const trafficHref = `/business/traffic?window=${encodeURIComponent(model.window.preset)}`;
  const upstreamId =
    node.kind === 'channel' || node.kind === 'upstream_model' ? node.upstreamId : null;
  return (
    <div className="flex flex-wrap gap-2 border-t border-line-soft pt-4">
      {node.kind === 'sku' && node.skuId !== null ? (
        <DeepLink to={`/market/${encodeURIComponent(node.skuId)}`} label={t('health_perspective.inspector.open_sku')} />
      ) : null}
      {upstreamId === null ? null : (
        <DeepLink
          to={`/supply/upstreams/${encodeURIComponent(upstreamId)}`}
          label={t('health_perspective.inspector.open_upstream')}
        />
      )}
      <DeepLink to={trafficHref} label={t('health_perspective.inspector.open_traffic')} />
      <DeepLink to="/supply/tenant-routing" label={t('health_perspective.inspector.open_routing')} />
    </div>
  );
}

function DeepLink({ to, label }: { readonly to: string; readonly label: string }): JSX.Element {
  return (
    <Link to={to} className={buttonClass('ghost', 'sm')}>
      {label}
      <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
    </Link>
  );
}

function Section({ caption, children }: { readonly caption: string; readonly children: ReactNode }): JSX.Element {
  return (
    <section className="flex min-w-0 flex-col gap-1.5">
      <h3 className="m-0 text-label text-paper-low">{caption}</h3>
      {children}
    </section>
  );
}

function Row({
  label,
  value,
  note,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly note?: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3" data-testid={testId}>
      <span className="min-w-0 truncate text-dense text-paper-mid">{label}</span>
      <span className="flex items-baseline gap-2">
        {note === undefined ? null : <span className="text-micro text-paper-low">{note}</span>}
        <span className="min-w-0 truncate font-mono text-dense text-paper">{value}</span>
      </span>
    </div>
  );
}
